import _pick from 'lodash/pick'
import _omit from 'lodash/omit'
import _isEqual from 'lodash/isEqual'
import qs from 'qs'
import { PROVIDERS } from 'dataSource/ProviderSearch/routes'
import format from 'date-fns/format'
import toDate from 'date-fns/toDate'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { isSplitReady, isSplitTimedOut } from 'utils/treatment'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import isEqual from 'lodash/isEqual'
import {
  generateDatesWithDayOfWeekFilter,
  getGenderValuesFromIds,
  getSlotsStartEndDate,
  hasSecondaryFilters,
  matchTimezoneFromList,
} from 'app/ProviderSearch/utils'
import useApi from 'hooks/useApi'
import {
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { CachePolicies } from 'use-http'
import { sessionStorageKeys, DayOfWeek } from 'constants/common'
import { useRouter } from 'next/router'
import useThriveAuth from 'hooks/useThriveAuth'
import { getCurrentZonnedTime, getParsedQuery, hasError } from 'utils/common'
import { resolveTimezoneByTzCode } from 'utils/timezone'
import { PAGINATION_LIMIT } from 'constants/api'
import {
  fetchTimezoneFromLocation,
  ProviderFilterTypes,
} from 'dataSource/ProviderSearch'
import { slotDate } from 'context/ProviderBooking/SlotDate'
import RudderStackContext from 'context/common/RudderStackContext'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import { clearTimezone, readTimezone } from 'utils/timezoneStorage'
import useIsRolloutPlanSettled from 'hooks/treatments/useIsRolloutPlanSettled'

type Args = {
  isDirectMatch0WithOnlyPrimaryFilter: MutableRefObject<boolean>
  groupedFilters: GroupedProviderFilter
  filterLookup: ProviderFilterLookup
  setSelectedPractice: (selectedPractice: Practice) => void
  secondaryFiltes?: string[]
  extendedBookingEnabled?: boolean
  extendedBookingLoading?: boolean
  timezoneContext: TimeZoneContextObject
}

type UseFetchProvidersListReturn = (
  args: Args
) => {
  providerList: Provider[]
  providerListLoading: boolean
  providerListError: AlertMessage
  providerListResult: Provider[]
  providerPagination: PaginationType
  intakeSupplyReserveData: Partial<IntakeSupplyReserveDetails>
}

const useFetchProvidersList: UseFetchProvidersListReturn = ({
  isDirectMatch0WithOnlyPrimaryFilter,
  groupedFilters,
  filterLookup,
  setSelectedPractice,
  secondaryFiltes = [],
  extendedBookingEnabled = false,
  extendedBookingLoading = false,
  timezoneContext,
}) => {
  const { request: apiRequest } = useApi({
    cachePolicy: CachePolicies.CACHE_FIRST,
  })
  const router = useRouter()
  const { user, isLoading, isSupportOrThirdPartyUser } = useThriveAuth()
  const [providerPagination, setProviderPagination] = useState<PaginationType>({
    count: 0,
    offset: 0,
  })

  // Monotonic id so a slower earlier /providers response (e.g. prior date-
  // paginator page while DOW re-rank is active) cannot overwrite newer state
  // or previousQueryRef.
  const latestProviderRequestId = useRef(0)
  // Last query whose response was applied to the provider list.
  const previousQueryRef = useRef<string>('')
  // Query for the in-flight /providers request (may differ from applied).
  const inFlightQueryRef = useRef<string>('')
  // Whether the last applied query had slotStartDate present in the URL.
  const previousHadExplicitSlotStartDateRef = useRef<boolean | null>(null)
  const previousExtendedBookingRef = useRef<boolean | null>(null)
  const [providerListError, setProviderListError] = useState<AlertMessage>(null)
  const [providerListResult, setProviderListResult] = useState<Provider[]>(null)
  const [intakeSupplyReserveData, setIntakeSupplyReserveData] = useState<
    Partial<IntakeSupplyReserveDetails>
  >({})
  const [providerListLoading, setProviderListLoading] = useState<boolean>(true)
  const [providerList, setProviderList] = useState<Provider[]>([])
  const { timeZone, ...restTimeZone } = timezoneContext
  const { slotListLimit } = slotDate(timeZone)
  const splitReadyFlag = isSplitReady()
  const splitTimedOut = isSplitTimedOut()
  const { analyticsConsentGranted } = useContext(RudderStackContext)
  const rolloutPlanSettled = useIsRolloutPlanSettled()

  const { query } = router
  const { state = '' } = query
  const supplyReserveTreatment = useGetTreatment(
    TreatmentFlags.SUPPLY_RESERVE,
    !!state,
    { state }
  )
  // With consent: wait for a real Split resolution (or SDK timeout) before
  // fetching so SUPPLY_RESERVE can add partnershipCode. Without consent: wait
  // for the rollout plan so a 100% released ON still controls the request.
  const canFetchWithSplit = analyticsConsentGranted
    ? (splitReadyFlag &&
        supplyReserveTreatment !== treatmentFlagValues.CONTROL) ||
      splitTimedOut
    : rolloutPlanSettled

  /**
   * Fetches providers with the selected filters.
   * And based on if filters is applied or not, either fetchPractices or fetchRestProvidersForPractice
   * Will be called to get the practices.
   */
  const fetchProviders = async (parsedQuery): Promise<void> => {
    // A newer request can start before this one resolves (e.g. gender
    // availability updates after a state change, or DOW date-paginator
    // advances), so only the latest may update state.
    const requestId = ++latestProviderRequestId.current
    const isLatestRequest = () => requestId === latestProviderRequestId.current
    // previousQueryRef must only advance after this request's results are applied.
    let didApplyResponse = false

    const urlSlotAtStart = getParsedQuery(router).slotStartDate as
      | string
      | string[]
      | undefined
    const requestHadExplicitSlotStartDate = Array.isArray(urlSlotAtStart)
      ? urlSlotAtStart.some((v) => v != null && v !== '')
      : urlSlotAtStart != null && urlSlotAtStart !== ''

    // Snapshot the in-flight query so A→B→A can detect a stale request for B.
    inFlightQueryRef.current = qs.stringify(
      _omit(parsedQuery, ['partnershipCode']),
      { skipNulls: true }
    )

    try {
      if (!hasSecondaryFilters(parsedQuery, secondaryFiltes)) {
        isDirectMatch0WithOnlyPrimaryFilter.current = false
      }

      if (!providerListLoading) {
        setProviderListLoading(true)
      }

      // Keep the prior list visible while a replacement request is in flight so a
      // discarded stale response cannot leave the UI empty.

      const {
        practiceId,
        slotStartDate,
        latitude,
        longitude,
        partnershipCode,
      } = parsedQuery

      // Read stored timezone directly from sessionStorage to avoid stale closure values
      // that can occur when this async function runs before React re-renders with the
      // hydrated timezone from sessionStorage.
      const storedTimezone = readTimezone()

      // Detect whether the user actively changed the search location (as opposed to
      // the first fetch after page mount where previousQueryRef resets to '').
      const hasPreviousQuery = previousQueryRef.current !== ''
      const parsedPreviousQuery = qs.parse(previousQueryRef.current)
      const locationChanged =
        parsedPreviousQuery.latitude !== parsedQuery.latitude ||
        parsedPreviousQuery.longitude !== parsedQuery.longitude

      // If a support user explicitly changed the search location (not just returning to the
      // same page), release the pinned timezone so the new location's timezone becomes the
      // default. This lets the location-based timezone apply for the new state/region.
      let effectiveStoredTimezone = storedTimezone

      if (
        isSupportOrThirdPartyUser &&
        hasPreviousQuery &&
        locationChanged &&
        storedTimezone
      ) {
        clearTimezone()
        effectiveStoredTimezone = null
      }

      // Use the effective stored timezone for the API call; fall back to the context
      // timezone (which may be stale on the first render, hence reading from storage above).
      let timezoneToUse = effectiveStoredTimezone?.tzCode ?? timeZone

      // Fetching the timezone from location for a support or third party support partner user.
      // Only apply location timezone when no user-selected timezone is active.
      if (
        isSupportOrThirdPartyUser &&
        !effectiveStoredTimezone &&
        locationChanged
      ) {
        const timezoneObjectFromLocation = await fetchTimezoneFromLocation(
          latitude,
          longitude
        )

        if (!isLatestRequest()) {
          return
        }

        timezoneToUse = timezoneObjectFromLocation.timeZoneId

        const matchingTimezone = matchTimezoneFromList(
          timezoneObjectFromLocation.timeZoneName,
          timezoneObjectFromLocation.timeZoneId
        )

        restTimeZone.updateTimezone(
          matchingTimezone ??
            resolveTimezoneByTzCode(
              timezoneObjectFromLocation.timeZoneId,
              timezoneObjectFromLocation.timeZoneName
            )
        )
      }

      const utcStartTime = zonedTimeToUtc(
        toDate(parseInt(slotStartDate)),
        Intl.DateTimeFormat().resolvedOptions().timeZone
      )

      const validDayOfWeekValues = Object.values(DayOfWeek) as string[]
      const rawDayOfWeek = parsedQuery.dayOfWeek as
        | string
        | string[]
        | undefined
      const normalizedDayOfWeek: DayOfWeek[] | undefined = rawDayOfWeek
        ? (Array.isArray(rawDayOfWeek)
            ? rawDayOfWeek
            : [rawDayOfWeek]
          ).filter((v): v is DayOfWeek => validDayOfWeekValues.includes(v))
        : undefined

      // DatePaginator anchors columns on the URL/paginator start. Extended
      // booking still uses "now" for the slots API window (listAnchor), but
      // rankingDates must follow the paginator so column sort matches the grid.
      const paginatorAnchor = utcToZonedTime(utcStartTime, timezoneToUse)
      const listAnchor = extendedBookingEnabled
        ? getCurrentZonnedTime(timezoneToUse)
        : paginatorAnchor

      const { startDate, endDate } = getSlotsStartEndDate(
        listAnchor,
        slotListLimit,
        timezoneToUse,
        normalizedDayOfWeek?.length ? normalizedDayOfWeek : undefined,
        undefined,
        extendedBookingEnabled
      )

      // THRIV-13687: Compute visible column dates for the column-based DOW sort.
      // These are the exact calendar dates (YYYY-MM-DD in search TZ) shown on
      // screen — sent to the server so rankings align with the displayed grid.
      const rankingDates: string[] | undefined = normalizedDayOfWeek?.length
        ? generateDatesWithDayOfWeekFilter(
            paginatorAnchor,
            slotListLimit,
            normalizedDayOfWeek,
            timezoneToUse,
            extendedBookingEnabled
          )
            .filter((d): d is Date => d !== null)
            .map((d) => format(d, 'yyyy-MM-dd'))
        : undefined

      const isSupplyReserveFlagEnabled =
        supplyReserveTreatment === treatmentFlagValues.ON
      const {
        id: b2bPartnerId,
        isActive: isB2bPartnerActive,
        partnershipCode: existingB2bPartnerKeyword,
      } = user.businessPartner ?? {}
      const isExistingB2bUser = isB2bPartnerActive && !!b2bPartnerId
      const b2bPartnershipCode = isExistingB2bUser
        ? existingB2bPartnerKeyword
        : partnershipCode

      const queryObj = {
        ..._pick(parsedQuery, [
          'state',
          'longitude',
          'latitude',
          'offset',
          'limit',
          'areaOfConcern',
          'insuranceId',
          'counselingType',
          'availabilityType',
          'orderBy',
          'gender',
          'clientAgeGroup',
          'language',
          'counselingApproach',
          'practiceId',
          'timeOfDay',
          'dayOfWeek',
          'excludeProviderId',
        ]),
        slotStartDate: startDate.toISOString(),
        slotEndDate: endDate.toISOString(),
        ...(isSupplyReserveFlagEnabled &&
          !!b2bPartnershipCode && { partnershipCode: b2bPartnershipCode }),
        ...((!!parsedQuery.timeOfDay || !!parsedQuery.dayOfWeek) && {
          timezone: timezoneToUse,
        }),
        // THRIV-13687: visible column dates for server-side DOW column sort
        ...(rankingDates?.length && { rankingDates }),
      }

      const qsQuery = qs.stringify(queryObj, {
        addQueryPrefix: true,
        skipNulls: true,
        indices: false,
      })
      const requestUrl = `${PROVIDERS}${qsQuery}`

      // If the practiceId is removed from url then remove from state as well
      if (!practiceId) {
        setSelectedPractice(null)
      }

      const responseProviderList = await apiRequest.get(requestUrl)

      // Drop stale responses from earlier paginator/filter requests.
      // URL/paginator changes bump latestProviderRequestId via the asPath effect,
      // so a superseded in-flight response exits here without applying.
      if (!isLatestRequest()) {
        return
      }

      const {
        result: providerListResult,
        pagination,
        intakeSupplyReserveDetails,
      } = responseProviderList
      hasError(apiRequest, responseProviderList)

      if (
        providerListResult?.length === 0 &&
        !hasSecondaryFilters(parsedQuery, secondaryFiltes)
      ) {
        isDirectMatch0WithOnlyPrimaryFilter.current = true
      }

      providerListResult?.forEach((provider: ListCardProvider) => {
        const { providerSlugs } = provider.practice
        provider.practice.providerSlugs = providerSlugs.map((slug) => ({
          name: slug,
        }))
        provider.filters = provider.availabilityTypeIds?.reduce(
          (filters, id) => [...filters, filterLookup[id]?.[0]],
          []
        )
      })

      setProviderList(providerListResult)
      setIntakeSupplyReserveData(intakeSupplyReserveDetails)
      setProviderListResult(providerListResult)
      setProviderPagination(pagination)
      didApplyResponse = true
    } catch (error) {
      if (isLatestRequest()) {
        // Drop retained cards on hard failure; they are only kept while a
        // replacement request is still in flight.
        setProviderList([])
        setProviderListResult(null)
        setProviderListError({
          timestamp: new Date().getTime(),
          message: error?.message,
        })
      }
    } finally {
      if (isLatestRequest()) {
        setProviderListLoading(false)

        if (didApplyResponse) {
          const parsedLatestQuery = _omit(parsedQuery, ['partnershipCode'])

          previousQueryRef.current = qs.stringify(parsedLatestQuery, {
            skipNulls: true,
          })
          previousHadExplicitSlotStartDateRef.current = requestHadExplicitSlotStartDate
          previousExtendedBookingRef.current = extendedBookingEnabled
          // Clear only when this request is still the latest; a replacement
          // request will have already overwritten inFlightQueryRef.
          inFlightQueryRef.current = ''
        }
      }
    }
  }

  const getProviderSearchQuery = () => {
    const qsQuery = getParsedQuery(router)
    const {
      offset = '0',
      limit = PAGINATION_LIMIT.toString(),
      slotStartDate = new Date().getTime().toString(),
      latitude,
      longitude,
      state,
      gender,
      ...restQuery
    } = qsQuery

    const genderValues = getGenderValuesFromIds(
      gender as string[],
      groupedFilters[ProviderFilterTypes.GENDER]
    )

    return {
      offset,
      limit,
      slotStartDate,
      latitude,
      longitude,
      state,
      ...(genderValues && { gender: genderValues }),
      ...restQuery,
    }
  }

  // The filter list only reaches the provider query through the gender names,
  // so a reload that leaves them unchanged must not fetch the providers again
  const selectedGenderValues =
    getGenderValuesFromIds(
      getParsedQuery(router).gender as string[],
      groupedFilters[ProviderFilterTypes.GENDER]
    )?.join(',') ?? ''

  useEffect(() => {
    // Used as asPath (i.e. router.asPath) instead of router query because
    // On initial load of the page when there is a query param in the url
    // the use effect is called twice while using router.query
    // first without the params and second with the params
    // so the api is called twice with 2 different params
    // asPath only updates once on page load and on url change
    const disableProviderFetch = sessionStorage.getItem(
      sessionStorageKeys.DISABLE_PROVIDER_FETCH_ONCE
    )
    const providerSearchQuery = getProviderSearchQuery()
    const hasLocationOnQuery =
      !!providerSearchQuery.state &&
      !!providerSearchQuery.latitude &&
      !!providerSearchQuery.longitude
    const parsedPreviousQuery = qs.parse(previousQueryRef.current)

    // THRIV-13687: When a partial DOW filter is active AND the URL has an
    // explicit slotStartDate, paginator advances must re-fetch so the server
    // re-ranks on the new first visible column. Otherwise ignore slotStartDate:
    // getProviderSearchQuery defaults a missing value to Date.now(), which
    // would look like a filter change on every effect run.
    // dayOfWeek comes through restQuery; cast because the return type of
    // getProviderSearchQuery only enumerates the explicitly named fields.
    const urlQuery = getParsedQuery(router)
    const rawDow = (providerSearchQuery as { dayOfWeek?: string | string[] })
      .dayOfWeek
    const hasDowFilter = Array.isArray(rawDow) ? rawDow.length > 0 : !!rawDow
    const rawUrlSlotStartDate = urlQuery.slotStartDate as
      | string
      | string[]
      | undefined
    const hasExplicitSlotStartDate = Array.isArray(rawUrlSlotStartDate)
      ? rawUrlSlotStartDate.some((v) => v != null && v !== '')
      : rawUrlSlotStartDate != null && rawUrlSlotStartDate !== ''
    // Null partnershipCode on both sides so it never looks like a filter change.
    // Keep offset in the comparison so pagination re-fetches (matches main).
    const nonLocationParams = {
      partnershipCode: null,
      ...(hasDowFilter && hasExplicitSlotStartDate
        ? {}
        : { slotStartDate: null }),
    }

    const currentQueryForCompare = {
      ...providerSearchQuery,
      ...nonLocationParams,
    }
    const isSameAsApplied = isEqual(
      {
        ...parsedPreviousQuery,
        ...nonLocationParams,
      },
      currentQueryForCompare
    )
    const parsedInFlightQuery = qs.parse(inFlightQueryRef.current)
    const isSameAsInFlight =
      inFlightQueryRef.current !== '' &&
      isEqual(
        {
          ...parsedInFlightQuery,
          ...nonLocationParams,
        },
        currentQueryForCompare
      )

    const extendedBookingChanged =
      previousExtendedBookingRef.current !== null &&
      previousExtendedBookingRef.current !== extendedBookingEnabled

    // DOW on: gaining/losing an explicit slotStartDate in the URL must refetch
    // even when defaulted timestamps make isSameAsApplied look equal.
    const explicitSlotPresenceChanged =
      hasDowFilter &&
      previousHadExplicitSlotStartDateRef.current !== null &&
      previousHadExplicitSlotStartDateRef.current !== hasExplicitSlotStartDate

    const needsProvidersForCurrentUrl =
      !isSameAsApplied || extendedBookingChanged || explicitSlotPresenceChanged
    // URL matches applied list but an older request is still in flight (A→B→A):
    // start a replacement so the stale response cannot apply.
    const needsReplaceStaleInFlight =
      isSameAsApplied && inFlightQueryRef.current !== '' && !isSameAsInFlight

    if (
      !isLoading &&
      !extendedBookingLoading &&
      !disableProviderFetch &&
      hasLocationOnQuery &&
      canFetchWithSplit &&
      ((needsProvidersForCurrentUrl && !isSameAsInFlight) ||
        needsReplaceStaleInFlight)
    ) {
      fetchProviders(providerSearchQuery)
    }

    sessionStorage.removeItem(sessionStorageKeys.DISABLE_PROVIDER_FETCH_ONCE)
  }, [
    router.asPath,
    selectedGenderValues,
    isLoading,
    splitReadyFlag,
    splitTimedOut,
    supplyReserveTreatment,
    analyticsConsentGranted,
    rolloutPlanSettled,
    extendedBookingEnabled,
    extendedBookingLoading,
  ])

  return {
    providerList,
    providerListLoading,
    providerListError,
    providerListResult,
    providerPagination,
    intakeSupplyReserveData,
  }
}

export default useFetchProvidersList
